import { useEffect } from "react";

// Deterrent only: blocks common DevTools / view-source shortcuts and the
// context menu. It must never clear the page, redirect, or poll window size —
// outerWidth/innerWidth gaps also change on zoom, on-screen keyboards (iPad),
// side panels and split view, which blanked the app for real users.

// selectstart can fire on a text node (e.g. inside a contenteditable editor),
// which has no tagName/closest, so step up to the parent element first.
const isEditable = (node) => {
  const el = node?.nodeType === 1 ? node : node?.parentElement;
  return (
    !!el &&
    (el.tagName === "INPUT" ||
      el.tagName === "TEXTAREA" ||
      el.isContentEditable ||
      !!el.closest?.("[contenteditable='true']"))
  );
};

const DevToolsBlocker = () => {
  useEffect(() => {
    const blockKeys = (e) => {
      const key = e.key?.toLowerCase();
      const ctrl = e.ctrlKey;
      const shift = e.shiftKey;

      const isDevToolsShortcut =
        e.key === "F12" ||
        (ctrl && shift && (key === "i" || key === "j" || key === "c"));

      // Ctrl+U (view source), Ctrl+S (save), Ctrl+P (print)
      const isPageShortcut = ctrl && !shift && ["u", "s", "p"].includes(key);

      // Ctrl+A only outside text fields, so users can still select their own text
      const isSelectAll = ctrl && key === "a" && !isEditable(e.target);

      if (isDevToolsShortcut || isPageShortcut || isSelectAll) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const blockContextMenu = (e) => {
      if (isEditable(e.target)) return;
      e.preventDefault();
    };

    // Block page text selection/drag, but never inside inputs — preventing
    // selectstart there breaks caret placement and editing on iPad.
    const blockSelectStart = (e) => {
      if (isEditable(e.target)) return;
      e.preventDefault();
    };

    const blockDragStart = (e) => {
      if (isEditable(e.target)) return;
      e.preventDefault();
    };

    document.addEventListener("keydown", blockKeys);
    document.addEventListener("contextmenu", blockContextMenu);
    document.addEventListener("selectstart", blockSelectStart);
    document.addEventListener("dragstart", blockDragStart);

    // Silence noisy logs, but keep warn/error so production issues stay visible
    const originalLog = console.log;
    const originalDebug = console.debug;
    const originalInfo = console.info;
    const noop = () => {};
    console.log = noop;
    console.debug = noop;
    console.info = noop;

    return () => {
      document.removeEventListener("keydown", blockKeys);
      document.removeEventListener("contextmenu", blockContextMenu);
      document.removeEventListener("selectstart", blockSelectStart);
      document.removeEventListener("dragstart", blockDragStart);

      console.log = originalLog;
      console.debug = originalDebug;
      console.info = originalInfo;
    };
  }, []);

  return null;
};

export default DevToolsBlocker;
